/* eslint-disable react/prop-types */
import React from 'react';
import { useTheme } from 'styled-components';
import IconTile from '../IconTile';
import { tokens } from '../tokens';
import useIsMobile from '../useIsMobile';

// Header bar — orange icon tile + title, with an optional right-aligned slot
// (children) for actions like the bell + account switcher.
//
// Two modes:
//  • Simple (default): one icon tile + a single-line string `title`.
//  • Lockup: pass `logo` (img src) and/or `subtitle` (node) to render a branded
//    lockup — logo beside a stacked title + subtitle. Used by the Weather Center
//    (Pit Pay wordmark + "Weather Center" + venue line). On mobile the lockup
//    centers and the tile is hidden.
//
// `tileSize` overrides the icon tile size (e.g. 64 for the Weather Center).
const PageHeader = ({ icon, title, subtitle, logo, tileSize, children }) => {
  const t = tokens(useTheme());
  const isMobile = useIsMobile();
  const hasLockup = !!(logo || subtitle);
  const mobileLockup = isMobile && hasLockup;

  const titleEl = (
    <h1
      style={{
        fontSize: hasLockup ? (isMobile ? 24 : 38) : isMobile ? 22 : 30,
        fontWeight: 800,
        color: t.text,
        margin: 0,
        letterSpacing: hasLockup ? '-0.8px' : '-0.5px',
        ...(hasLockup ? { lineHeight: 1.05 } : {}),
      }}
    >
      {title}
    </h1>
  );

  const lockup = (
    <div
      style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'center' : 'flex-start',
        gap: isMobile ? 10 : 17,
        minWidth: 0,
      }}
    >
      {logo ? (
        <img
          src={logo}
          alt=""
          style={{
            height: isMobile ? 38 : 56,
            width: 'auto',
            display: 'block',
            flexShrink: 0,
            marginTop: isMobile ? 0 : 1,
          }}
        />
      ) : null}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: subtitle ? 8 : 0,
          minWidth: 0,
          alignItems: isMobile ? 'center' : 'flex-start',
        }}
      >
        {titleEl}
        {subtitle ? (
          <div
            style={{
              fontSize: isMobile ? 12.5 : 15,
              color: t.sub,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 7,
              flexWrap: 'wrap',
              justifyContent: isMobile ? 'center' : 'flex-start',
            }}
          >
            {subtitle}
          </div>
        ) : null}
      </div>
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: mobileLockup ? 'column' : 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: isMobile ? 12 : 18,
        background: t.card,
        border: '1px solid ' + t.border,
        borderRadius: 16,
        padding: isMobile ? '16px' : '18px 22px 18px 26px',
        marginBottom: isMobile ? 16 : 24,
        textAlign: mobileLockup ? 'center' : 'left',
      }}
    >
      {mobileLockup ? null : (
        <IconTile icon={icon} size={tileSize || (isMobile ? 40 : 46)} />
      )}
      {hasLockup ? lockup : titleEl}
      {children ? (
        <div
          style={{
            marginLeft: isMobile ? 0 : 'auto',
            width: isMobile ? '100%' : 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: isMobile ? 'center' : 'flex-start',
            gap: 12,
            flexWrap: 'wrap',
            ...(mobileLockup
              ? { borderTop: '1px solid ' + t.border, paddingTop: 14 }
              : {}),
          }}
        >
          {children}
        </div>
      ) : null}
    </div>
  );
};

export default PageHeader;
